import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FirestoreError, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { db } from "../lib/firebase";
import { AppFooter } from "../components/AppFooter";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

// Reached only once, right after a brand-new Google sign-in: Google gives us
// an email and a display name, but not the username the pairing flow needs
// to find you — so unlike email/password registration, the Firebase Auth
// account already exists here and nothing needs to be rolled back if the
// chosen username turns out to be taken; the user just tries another one.
export function CompleteProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const usernameLower = username.trim().toLowerCase();
    if (!USERNAME_RE.test(usernameLower)) {
      setError("Benutzername: 3–20 Zeichen, nur Kleinbuchstaben, Ziffern und Unterstrich.");
      return;
    }
    if (!user) return;

    setSubmitting(true);
    try {
      await setDoc(doc(db, "usernames", usernameLower), { uid: user.uid });
      await setDoc(doc(db, "users", user.uid), {
        username: username.trim(),
        usernameLower,
        displayName: displayName.trim() || username.trim(),
        createdAt: serverTimestamp(),
      });
      navigate("/pair");
    } catch (err) {
      if (err instanceof FirestoreError && err.code === "permission-denied") {
        setError("Dieser Benutzername ist schon vergeben. Bitte einen anderen wählen.");
      } else {
        setError("Konnte nicht gespeichert werden. Bitte erneut versuchen.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-dvh bg-cream-50 dark:bg-night-bg">
      <div className="hero-glow flex flex-col items-center justify-end px-6 pb-14 pt-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8 text-cream-50">
            <path d="M12 21s-7.5-4.6-10.3-9.3C.1 9.1 1 5.6 4 4.3c2.3-1 4.7-.1 6 1.8l2 2.9 2-2.9c1.3-1.9 3.7-2.8 6-1.8 3 1.3 3.9 4.8 2.3 7.4C19.5 16.4 12 21 12 21Z" />
          </svg>
        </div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-cream-50">Fast geschafft</h1>
        <p className="mt-1.5 text-sm text-plum-100/80">Noch ein Benutzername für Wir</p>
      </div>

      <div className="mx-auto -mt-8 max-w-md px-6 pb-12">
        <form onSubmit={handleSubmit} className="card animate-fade-up space-y-4 rounded-3xl p-6" noValidate>
          <div>
            <label htmlFor="cp-name" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Dein Name
            </label>
            <input
              id="cp-name"
              type="text"
              required
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>
          <div>
            <label htmlFor="cp-username" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              Benutzername
            </label>
            <input
              id="cp-username"
              type="text"
              required
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="z.B. mia23"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
            <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">
              Darüber findet dich dein Partner/deine Partnerin später zum Verbinden.
            </p>
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-base font-semibold text-cream-50 shadow-sm transition-all hover:bg-plum-800 active:scale-[0.985] disabled:opacity-60"
          >
            {submitting ? "Wird gespeichert…" : "Weiter"}
          </button>
        </form>
        <AppFooter />
      </div>
    </main>
  );
}
